<template>
    <DrawerRoot v-model:open="visible">
        <DrawerPortal>
            <DrawerOverlay class="fixed inset-0 z-[30] bg-black/80" />
            <DrawerContent
                aria-describedby="undefined"
                class="bg-card border-stroke-1 fixed inset-x-0 bottom-0 z-[31] flex h-[90vh] flex-col rounded-t-lg border-t-1 border-r-1 border-l-1"
            >
                <DrawerTitle class="sr-only">{{ $t('general.game_explanation') }}</DrawerTitle>
                <DrawerHandle class="mx-auto mt-2 mb-2 h-1 w-10 rounded-full bg-zinc-600" />
                <div class="flex-1 overflow-y-auto pt-2">
                    <Carousel
                        ref="GameExplanationCarousel"
                        :items-to-show="1"
                        :wrap-around="false"
                        :touch-drag="false"
                        :transition="300"
                        @slide-start="currentSlideIndex = $event.slidingToIndex"
                    >
                        <slide v-for="(game, index) in games" :key="index">
                            <div class="px-4">
                                <GameExplanationContent :game="game" />
                            </div>
                        </slide>
                    </Carousel>
                </div>
                <div class="bg-card pb-safe">
                    <div class="mb-4 flex space-x-3 px-4">
                        <DiagonalCutButton
                            v-if="isInfoOnly"
                            @click="close()"
                            :disabled="false"
                            variant="primary"
                            cut-position="left-top"
                        >
                            <span class="mt-0.5">{{ $t('general.close') }}</span>
                        </DiagonalCutButton>
                        <DiagonalCutButton
                            v-else
                            @click="handlePlayGame()"
                            :disabled="false"
                            variant="primary"
                            cut-position="left-top"
                        >
                            <span class="mt-0.5">{{ $t('general.play_game') }}</span>
                            <svg
                                class="size-3.5"
                                viewBox="0 0 9 14"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M5.58563 6.99992L0.292742 12.2928L1.70696 13.707L8.41406 6.99992L1.70695 0.292818L0.292741 1.70703L5.58563 6.99992Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </DiagonalCutButton>
                    </div>
                </div>
            </DrawerContent>
        </DrawerPortal>
    </DrawerRoot>
</template>

<script>
import 'vue3-carousel/carousel.css'
import { Carousel, Slide } from 'vue3-carousel'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import { mapStores } from 'pinia'
import { useMatchStore } from '@/stores/match.js'
import GameExplanationContent from '@/components/GameExplanationContent.vue'
import { useAuthStore } from '@/stores/auth.js'
import {
    DrawerRoot,
    DrawerPortal,
    DrawerOverlay,
    DrawerContent,
    DrawerHandle,
    DrawerTitle,
} from 'vaul-vue'

export default {
    components: {
        Carousel,
        Slide,
        DiagonalCutButton,
        DrawerRoot,
        DrawerPortal,
        DrawerOverlay,
        DrawerContent,
        DrawerHandle,
        DrawerTitle,
        GameExplanationContent,
    },
    data() {
        return {
            visible: false,
            selectedGame: 'x01',
            isInfoOnly: false,
            currentSlideIndex: 0,
            pendingSlideIndex: null,
            gameExplanationHandler: null,
        }
    },
    watch: {
        visible(isOpen) {
            if (isOpen && this.pendingSlideIndex !== null) {
                this.$nextTick(() => {
                    this.$refs['GameExplanationCarousel']?.slideTo(this.pendingSlideIndex, true)
                    this.pendingSlideIndex = null
                })
            }
        },
    },
    mounted() {
        this.gameExplanationHandler = (data) => {
            this.isInfoOnly = data?.infoOnly || false

            if (data?.key) {
                const desiredIndex = this.games.findIndex((g) => {
                    return g.key === data.key || g.content?.type === data.key
                })

                if (desiredIndex < 0) {
                    this.$toast(
                        this.$t('general.something_went_wrong'),
                        5000,
                        this.$t('general.explanation_unavailable'),
                    )
                    return
                }

                this.pendingSlideIndex = desiredIndex
                this.currentSlideIndex = desiredIndex
            }

            this.open()
        }

        this.$serviceBus.on('open-game-explanation', this.gameExplanationHandler)
    },
    beforeUnmount() {
        if (this.gameExplanationHandler) {
            this.$serviceBus.off('open-game-explanation', this.gameExplanationHandler)
            this.gameExplanationHandler = null
        }
    },
    methods: {
        open() {
            this.visible = true
        },
        close() {
            this.visible = false
        },
        previousSlide() {
            this.$refs['GameExplanationCarousel'].prev()
        },
        nextSlide() {
            this.$refs['GameExplanationCarousel'].next()
        },
        async handlePlayGame() {
            const currentGame = this.games[this.currentSlideIndex]

            if (currentGame) {
                this.close()

                const gameKeyToName = {
                    killer: 'Killer',
                    camelrace: 'Camel Race',
                    cricket: 'Tactics / Cricket',
                    x01: 'x01',
                }

                const gameTypeName = gameKeyToName[currentGame.key] || currentGame.name

                try {
                    await this.matchStore.selectMatchType(
                        gameTypeName,
                        this.authStore,
                        this.$router,
                        this.$toast,
                    )
                } catch (error) {
                    // Error handling already done in selectMatchType
                    console.error('Failed to create match:', error)
                }
            }
        },
    },
    computed: {
        ...mapStores(useMatchStore, useAuthStore),

        games() {
            return this.$gameService.list()
        },
    },
}
</script>
